import { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { RelatedArticleCard, RelatedArticle } from './RelatedArticleCard';
import { CarouselDots } from './CarouselDots';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { useContentHover } from '@/hooks/useContentHover';

// ⚠️ ANALYTICS: Card hover wrapper for related article cards
const RelatedCardWrapper = ({ article, index, children }: {
  article: { id: string; title: string; slug?: string };
  index: number;
  children: React.ReactNode;
}) => {
  const { contentHoverProps } = useContentHover({
    contentId: article.slug || article.id,
    contentType: 'card',
    contentTitle: article.title,
    contentSection: 'related-articles',
    contentPage: 'Article',
    contentPosition: index,
  });

  return <div {...contentHoverProps}>{children}</div>;
};
export interface RelatedArticlesSectionProps {
  /** Array of exactly 3 related articles */
  articles: [RelatedArticle, RelatedArticle, RelatedArticle];
  /** Additional className */
  className?: string;
  /** ⚠️ ANALYTICS: Callback when a related article is clicked */
  onArticleClick?: (article: RelatedArticle, position: number) => void;
}

/**
 * section-related-articles
 * 
 * A full-width section displaying 3 related article cards.
 * Only renders if all 3 articles are provided.
 * 
 * Tokens applied:
 * - Background: bg-platinum (breaks from white article background)
 * - Header: text-32 font-bold text-carbon
 * - Grid: 3-column on desktop, carousel on mobile with peek effect
 * - Padding: Standard section padding (py-16 px-4)
 */
export function RelatedArticlesSection({ articles, className = '', onArticleClick }: RelatedArticlesSectionProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
    breakpoints: {
      '(min-width: 768px)': { active: false },
    },
  });

  // ⚠️ ANALYTICS: Section visibility tracking
  const { ref: sectionRef } = useSectionVisibility({
    sectionId: 'related-articles',
    sectionName: 'Next Up',
    pageName: 'Article',
    position: 10,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  // Set up scroll listener
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  // Only render if we have exactly 3 articles with valid data
  if (!articles || articles.length !== 3 || articles.some(a => !a || !a.id)) {
    return null;
  }

  return (
    <section ref={sectionRef} className={`bg-platinum py-16 px-4 ${className}`}>
      <div className="max-w-6xl mx-auto">
        {/* Section header - mb-8 matches Learn page carousel spacing */}
        <h2 className="text-32 font-bold text-carbon mb-8">
          Next Up
        </h2>

        {/* Desktop: 3-column grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-8">
          {articles.map((article, index) => (
            <RelatedCardWrapper key={article.id} article={article} index={index}>
              <RelatedArticleCard 
                article={article}
                onClick={onArticleClick ? () => onArticleClick(article, index + 1) : undefined}
              />
            </RelatedCardWrapper>
          ))}
        </div>

        {/* Mobile: Carousel with peek effect */}
        <div className="md:hidden">
          <div className="overflow-hidden -mx-4" ref={emblaRef}>
            <div className="flex embla-gesture-lock">
              {articles.map((article, index) => (
                <div 
                  key={article.id} 
                  className="flex-shrink-0 pl-4"
                  style={{ width: 'calc(85% + 8px)' }}
                >
                  <RelatedCardWrapper article={article} index={index}>
                    <RelatedArticleCard 
                      article={article}
                      onClick={onArticleClick ? () => onArticleClick(article, index + 1) : undefined}
                    />
                  </RelatedCardWrapper>
                </div>
              ))}
              {/* Right padding spacer */}
              <div className="flex-shrink-0 w-4" />
            </div>
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center mt-6">
            <CarouselDots
              totalSlides={articles.length}
              selectedIndex={selectedIndex}
              onDotClick={scrollTo}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default RelatedArticlesSection;
